import { motion } from 'framer-motion';
import { ArrowUp, Heart, Code2 } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Projects', href: '#projects' },
    { label: 'Skills', href: '#skills' },
    { label: 'Contact', href: '#contact' },
  ];

  const services = [
    { label: 'AI Solutions', href: '#' },
    { label: 'Full-Stack Development', href: '#' },
    { label: 'Cloud Infrastructure', href: '#' },
    { label: 'Enterprise Architecture', href: '#' },
  ];

  const social = [
    { label: 'Website', href: 'https://techsierrasolutions.com' },
    { label: 'LinkedIn', href: 'https://linkedin.com/company/techsierra' },
    { label: 'Instagram', href: 'https://instagram.com/techsierra' },
    { label: 'Email', href: 'mailto:info@techsierrasolutions.com' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-gradient-to-b from-gray-100 to-gray-200 dark:from-dark-900 dark:to-dark-950 border-t border-gray-200 dark:border-dark-800 overflow-hidden">
      {/* Decorative wave */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />

      {/* Background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-secondary-500/5 rounded-full blur-3xl" />
      </div>

      <div className="section-container relative z-10 py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="col-span-1 md:col-span-2 lg:col-span-1"
          >
            <div className="flex items-center gap-2 mb-4">
              <Code2 className="w-8 h-8 text-primary-400" />
              <h3 className="text-2xl font-bold gradient-text">TechSierra</h3>
            </div>
            <p className="text-gray-600 dark:text-dark-400 mb-6 leading-relaxed">
              Elite technology solutions provider specializing in AI-powered applications,
              full-stack architecture, and cloud infrastructure.
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2 text-gray-500 dark:text-dark-500">
                <span>Built with</span>
                <Heart className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" />
                <span>and precision</span>
              </div>
              <div className="text-gray-400 dark:text-dark-600">
                <a href="https://techsierrasolutions.com" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-400 transition-colors font-medium">Tech Sierra Solutions</a>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-100 mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-600 dark:text-dark-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-primary-400 group-hover:w-4 transition-all duration-300" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-100 mb-4">Services</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href={service.href}
                    className="text-gray-600 dark:text-dark-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-primary-400 group-hover:w-4 transition-all duration-300" />
                    {service.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-100 mb-4">Connect</h4>
            <ul className="space-y-3">
              {social.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 dark:text-dark-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-primary-400 group-hover:w-4 transition-all duration-300" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="pt-8 border-t border-gray-300 dark:border-dark-800 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-gray-500 dark:text-dark-500 text-sm text-center md:text-left">
            &copy; {currentYear} Tech Sierra Solutions. All rights reserved.
          </p>

          <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-dark-500">
            <a href="#" className="hover:text-primary-400 transition-colors duration-300">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-primary-400 transition-colors duration-300">
              Terms of Service
            </a>
          </div>
        </motion.div>
      </div>

      {/* Back to top button */}
      <motion.button
        onClick={scrollToTop}
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.1, y: -5 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-8 right-8 p-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 group"
        aria-label="Back to top"
      >
        <ArrowUp className="w-6 h-6 group-hover:animate-bounce" />
      </motion.button>
    </footer>
  );
};

export default Footer;
